/**
 * Copyright IBM Corp. 2022, 2026
 */

import { MarketoFormProps } from '.'
import { removeLocaleFromPathname } from '@web/utils/supported-locales'
import { convertToRESTFields, STORED_FIELD_NAMES } from '../utils'
import { MktoForms2FormType } from './types'
import { MarketoSubmissionResponse } from '../types'
import s from './form.module.css'

/**
 * Prepares an embedded Marketo form for display,
 * intended to be run in the callback of
 * `MktoForms2.loadForm()` in the `MarketoForm`
 * component. It does the following to prepare
 * the passed Marketo form:
 * - Disables all Marketo-provided CSS stylesheets
 * - Applies custom styling we've written either
 *   in this file or in the `form.module.css` file
 *   alongside this one
 * - Sets initial values for the form, passed from
 *   props and/or initialized from cookies
 * - Sets up code and passed callbacks to run on
 *   form submission start, success, and error.
 *   Of note, error logic including `onSubmitError()`
 *   runs within the `form.onSubmit()` callback, as
 *   there's no `form.onError()` callback provided
 *   by the Marketo Forms API.
 *
 * This function is only intended to be run in
 * the `MarketoForm` component at
 * `components/marketo-form/form/index.tsx`.
 * `prepareForm()` lives in its own file for
 * clarity and readability only.
 *
 * Editing this function will affect the behavior
 * of every form on hashicorp.com, except the cloud
 * maturity quiz form at `components/stepped-form`
 * as that is part of a bespoke form implementation.
 * @param form Marketo form object
 * @param formProps Props on the form's component
 * @param textForRequiredLabels Text to use on labels that would say "Required"
 * @param loadCallback Callback to run after the form is loaded, at the end of this function
 */
export function prepareForm(
	form: MktoForms2FormType,
	formProps: MarketoFormProps,
	textForRequiredLabels?: string | null,
	loadCallback?: () => void,
) {
	const formElement = form.getFormElem()[0]
	const formElementId = form.getFormElem()[0].id.split('_')[1]

	const {
		className,
		allowBadges = true,
		formName,
		initialValues,
		persistFieldValues,
		submitTitle,
		onSubmitError,
		onSubmitStart,
		onSubmitSuccess,
		errorText,
	} = formProps

	// Remove the stylesheets

	// Generate list of stylesheets to remove
	const mktoStyleSheetHref = [
		`http://${process.env.MARKETO_BASE_URL}/js/forms2/css/forms2.css`,
		`http://${process.env.MARKETO_BASE_URL}/js/forms2/css/forms2-theme-inset.css`,
		`http://${process.env.MARKETO_BASE_URL}/js/forms2/css/forms2-theme-simple.css`,
		`http://${process.env.MARKETO_BASE_URL}/js/forms2/css/forms2-theme-glow.css`,
		`https://${process.env.MARKETO_BASE_URL}/js/forms2/css/forms2.css`,
		`https://${process.env.MARKETO_BASE_URL}/js/forms2/css/forms2-theme-inset.css`,
		`https://${process.env.MARKETO_BASE_URL}/js/forms2/css/forms2-theme-simple.css`,
		`https://${process.env.MARKETO_BASE_URL}/js/forms2/css/forms2-theme-glow.css`,
	]

	// Disable each sheet in the list
	Array.from(document.styleSheets).forEach((styleSheet) => {
		if (mktoStyleSheetHref.includes(styleSheet.href)) {
			styleSheet.disabled = true
		}
	})

	// Remove the style attribute
	Array.from(formElement.querySelectorAll('[style]'))
		.concat(formElement)
		.forEach((element) => {
			element.removeAttribute('style')
		})

	// Scope input field attributes to current form's ID
	Array.from(formElement.querySelectorAll('input, select')).forEach(
		(element) => {
			if (element.hasAttribute('id')) {
				element.setAttribute(
					'id',
					`${element.getAttribute('id')}_${formElementId}`,
				)

				if (element.hasAttribute('aria-labelledby')) {
					element.setAttribute(
						'aria-labelledby',
						element
							.getAttribute('aria-labelledby')
							.replace(
								`Lbl${element.getAttribute('name')}`,
								`Lbl${element.getAttribute('name')}_${formElementId}`,
							),
					)
				}
			}
		},
	)

	// Scope field labels to form's ID
	Array.from(formElement.querySelectorAll('label')).forEach((element) => {
		element.setAttribute('id', `${element.getAttribute('id')}_${formElementId}`)

		element.setAttribute(
			'for',
			`${element.getAttribute('for')}_${formElementId}`,
		)
	})

	// Add a stylable class name to the form element root
	formElement.classList.add(s['mktoForm-root'])

	// Add optional classNames to the form element root
	if (className) {
		formElement.classList.add(className)
	}

	// Add optional `aria-label` if `formName` is set
	if (formName) {
		formElement.setAttribute('aria-label', formName)
	}

	// Remove the default styles
	document.querySelectorAll('style').forEach((element) => {
		if (element.innerHTML.includes('mktoForm')) {
			element.remove()
		}
	})

	if (allowBadges) {
		formElement.classList.add('badges-allowed')
	}

	// Set 'Required' text in badges
	formElement.setAttribute(
		'style',
		`--required-text: '${textForRequiredLabels}';`,
	)

	// Apply classes and styles to button
	const submitButton = formElement.querySelector('.mktoButton')
	submitButton.setAttribute('class', s['mktoForm-submit'])
	submitButton.innerHTML = `<div class="${s['mktoForm-submit-text']}">${
		submitTitle ?? submitButton.textContent
	}</div>`

	// Set initial values for the form fields
	if (initialValues) {
		form.setValues(initialValues)

		const storedValues = persistFieldValues
			? JSON.parse(localStorage.getItem('marketoFormData') || '{}')
			: null
		const newValues = {
			...initialValues,
			...storedValues,
		}
		Object.entries(newValues).forEach(([fieldId, value]) => {
			if (!form.getValues()[fieldId]) {
				form.setValues({ [fieldId]: value })
			}
		})
	}

	/**
	 * If there's a blank form_page_url__c field in the form,
	 * fill it with the current, locale-less URL.
	 *
	 * Borrowed and modified from original code in the
	 * FormPageURLField form partial component at:
	 * https://github.com/hashicorp/web/blob/main/apps/www/components/marketo-form/partials/fields/form-page-url-field/index.tsx
	 *
	 * Context from the above file:
	 * Marketo campaigns expect a URL that does not include the locale.
	 * This is because the campaigns were created prior to locales being
	 * added to hashicorp.com paths. As a result, we need to strip out the
	 * locale from the path in order for Marketo campaigns to work.
	 * In the future, we should better understand why this dependency exists
	 * and explore ways to make this less brittle.
	 */
	if (Object.keys(form.getValues()).includes('form_page_url__c')) {
		const { protocol, host, pathname } = window.location
		const pathnameWithoutLocale = removeLocaleFromPathname(pathname)
		const url = `${protocol}//${host}${pathnameWithoutLocale}`
		form.setValues({ form_page_url__c: url })
	}

	// Callbacks
	form.onSubmit(async () => {
		if (onSubmitStart) {
			onSubmitStart()
		}

		// The following code does a quick POST to the Marketo API to check the response this form would generate.
		// If there are any errors in the result, we notify the #marketo-golive slack channel.
		const leadFormFields = convertToRESTFields(form.getValues())
		const marketoRequestBody = {
			input: [
				{
					leadFormFields,
					visitorData: {
						pageURL: window.location.href,
					},
				},
			],
			formId: form.getId(),
			munchkinId: process.env.MARKETO_MUNCHKIN_ID,
		}

		try {
			// Don't actually do anything with these variables;
			// we're just checking that the response is successful.
			// Per testing, Marketo will dedupe this submission from the "real" one that is submitted in the embed.
			const res = await fetch(`${window.location.origin}/api/marketo/submit`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(marketoRequestBody),
			})

			const marketoResponse = (await res.json()) as MarketoSubmissionResponse
		} catch (err) {
			// If the responses above were unsuccessful, we will have an error in submission.
			// We run onSubmitError() if passed to MarketoForm.
			// Code at the API route handles running notifyError() to notify the #marketo-golive Slack channel.
			if (onSubmitError) {
				onSubmitError()
			}
		}
	})

	form.onSuccess(() => {
		const dataToPersist = Object.fromEntries(
			Object.entries(form.getValues()).filter(([k]) =>
				STORED_FIELD_NAMES.includes(k),
			),
		)

		localStorage.setItem('marketoFormData', JSON.stringify(dataToPersist))

		if (onSubmitSuccess) {
			onSubmitSuccess(dataToPersist, form.getValues())
		}

		// Prevents Marketo from redirecting the user and/or refreshing the page
		return false
	})

	if (errorText) {
		form.showErrorMessage(
			`<svg class="flight-icon flight-icon-alert-triangle-fill flight-icon-display-inline" aria-hidden="true" fill="currentColor" id=":r18:" width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" style="margin-right: 8px; vertical-align: text-bottom;"><use href="/_next/static/media/svg-sprite.e33d3510.svg#flight-alert-triangle-fill-16"></use></svg>${errorText}`,
		)
	}

	if (loadCallback) {
		loadCallback()
	}
}
